'use client';

import { EmptyState, LoadingSpinner, SectionHeader } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { supplierMenuApi, type PublicProductListItem } from '@/lib/api/supplierMenu';
import { useSuppliers } from '@/lib/hooks/useSuppliers';
import { getSupplierDisplayName } from '@/lib/utils/supplierUtils';
import { cssVars } from '@/styles/theme';
import { Search as SearchIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

export default function SearchPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('pages.search');
  const isRTL = locale === 'ar';
  const { user } = useAuth();
  const isGuest = !user;
  const { data: suppliersData } = useSuppliers();

  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState<'all' | 'services' | 'suppliers'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [productResults, setProductResults] = useState<PublicProductListItem[]>([]);

  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    let mounted = true;
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const response = await supplierMenuApi.listPublicProducts({
          page: 1,
          limit: 24,
          search: initialQuery || undefined,
        });
        if (mounted) {
          setProductResults(response.data ?? []);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadProducts();
    return () => {
      mounted = false;
    };
  }, [initialQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/${locale}/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const filteredResults = useMemo(() => {
    const query = initialQuery.trim().toLowerCase();
    const filteredProducts = query
      ? productResults.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            (p.description ?? '').toLowerCase().includes(query) ||
            (p.supplier?.name ?? '').toLowerCase().includes(query)
        )
      : productResults;

    const filteredSuppliers = suppliersData
      .filter((supplier) => {
        if (!query) return true;
        const supplierName = getSupplierDisplayName(supplier, '').toLowerCase();
        return (
          supplierName.includes(query) ||
          (supplier.serviceDescription ?? '').toLowerCase().includes(query) ||
          supplier.categories?.some(
            (cat) =>
              cat.category?.nameAr?.toLowerCase().includes(query) ||
              cat.category?.nameEn?.toLowerCase().includes(query)
          )
        );
      })
      .map((supplier) => ({
        id: supplier.id,
        name: getSupplierDisplayName(supplier),
        avatar: supplier.user?.avatar || '/images/placeholders/service-placeholder.jpg',
        serviceDescription: supplier.serviceDescription ?? '',
        rating: supplier.user?.averageRating ?? 0,
        totalReviews: supplier.user?.totalReviews ?? 0,
        completedProjects: 0,
        isVerified: supplier.isVerified,
      }));

    if (activeTab === 'services') {
      return { products: filteredProducts, suppliers: [] };
    }
    if (activeTab === 'suppliers') {
      return { products: [], suppliers: filteredSuppliers };
    }
    return { products: filteredProducts, suppliers: filteredSuppliers };
  }, [activeTab, initialQuery, productResults, suppliersData]);

  const totalResults = filteredResults.products.length + filteredResults.suppliers.length;

  return (
    <div
      className="min-h-screen py-20"
      style={{
        backgroundColor: cssVars.neutral.bg,
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <SectionHeader
            badge={t('badge')}
            title={t('title')}
            subtitle={
              initialQuery ? `${t('resultsFor')}: "${initialQuery}"` : t('searchPlaceholder')
            }
            variant="light"
            align={isRTL ? 'right' : 'left'}
            badgeColor="warm"
            className="max-w-3xl"
          />
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <SearchIcon
              className="absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2"
              style={{ color: cssVars.neutral.textSecondary }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full rounded-2xl border-2 py-4 pe-4 ps-12 text-lg transition-all focus:outline-none focus:ring-2"
              style={{
                backgroundColor: cssVars.neutral.surface,
                borderColor: cssVars.neutral.border,
              }}
            />
            <button
              type="submit"
              className="absolute end-2 top-1/2 -translate-y-1/2 rounded-xl px-6 py-2 font-bold transition-opacity hover:opacity-90"
              style={{
                backgroundColor: cssVars.primary.DEFAULT,
                color: 'white',
              }}
            >
              {t('search')}
            </button>
          </div>
        </form>

        {/* Tabs */}
        <div className="mb-6 flex gap-2 border-b-2" style={{ borderColor: cssVars.neutral.border }}>
          {[
            { id: 'all', label: t('all'), count: totalResults },
            {
              id: 'services',
              label: locale === 'ar' ? 'المنتجات' : 'Products',
              count: filteredResults.products.length,
            },
            {
              id: 'suppliers',
              label: t('suppliers'),
              count: filteredResults.suppliers.length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className="relative px-4 py-3 font-semibold transition-colors"
              style={{
                color:
                  activeTab === tab.id ? cssVars.primary.DEFAULT : cssVars.neutral.textSecondary,
              }}
            >
              {tab.label} ({tab.count})
              {activeTab === tab.id && (
                <div
                  className="absolute bottom-0 end-0 start-0 h-1 rounded-t"
                  style={{ backgroundColor: cssVars.primary.DEFAULT }}
                />
              )}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!isLoading && totalResults === 0 && (
          <EmptyState
            icon={SearchIcon}
            title={t('noResults')}
            description={t('tryDifferentTerms')}
          />
        )}

        {!isLoading && totalResults > 0 && (
          <div className="space-y-12">
            {/* Products Results */}
            {filteredResults.products.length > 0 && (
              <div>
                <h2
                  className="mb-6 text-2xl font-bold"
                  style={{ color: cssVars.secondary.DEFAULT }}
                >
                  {locale === 'ar' ? 'المنتجات' : 'Products'} ({filteredResults.products.length})
                </h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredResults.products.map((product) => (
                    <Link
                      key={product.id}
                      href={`/${locale}/products/${product.id}?supplier=${product.supplierId}`}
                      className="overflow-hidden rounded-3xl border-2 transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: cssVars.neutral.surface,
                        borderColor: cssVars.neutral.border,
                      }}
                    >
                      <img
                        src={product.imageUrl || '/images/placeholders/service-placeholder.jpg'}
                        alt={product.name}
                        className="h-44 w-full bg-gray-100 object-contain"
                      />
                      <div className="space-y-3 p-5">
                        <h3
                          className="text-lg font-bold"
                          style={{ color: cssVars.secondary.DEFAULT }}
                        >
                          {product.name}
                        </h3>
                        <p
                          className="line-clamp-2 text-sm"
                          style={{ color: cssVars.neutral.textSecondary }}
                        >
                          {product.description}
                        </p>
                        <p
                          className="text-sm font-medium"
                          style={{ color: cssVars.neutral.textSecondary }}
                        >
                          {locale === 'ar' ? 'المورد:' : 'Supplier:'} {product.supplier?.name}
                        </p>

                        {isGuest ? (
                          <p className="text-sm" style={{ color: cssVars.primary.DEFAULT }}>
                            {locale === 'ar'
                              ? 'سجل الدخول لعرض السعر والطلب'
                              : 'Sign in to view pricing and place order'}
                          </p>
                        ) : (
                          <div
                            className="space-y-1 text-sm"
                            style={{ color: cssVars.neutral.textSecondary }}
                          >
                            <p>
                              {locale === 'ar' ? 'السعر:' : 'Price:'}{' '}
                              <span
                                className="font-semibold"
                                style={{ color: cssVars.secondary.DEFAULT }}
                              >
                                {product.price} {product.currency}
                              </span>
                            </p>
                            <p>
                              {locale === 'ar' ? 'أقل كمية:' : 'Min quantity:'}{' '}
                              {product.minOrderQuantity}
                            </p>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Suppliers Results */}
            {filteredResults.suppliers.length > 0 && (
              <div>
                <h2
                  className="mb-6 text-2xl font-bold"
                  style={{ color: cssVars.secondary.DEFAULT }}
                >
                  {t('suppliers')} ({filteredResults.suppliers.length})
                </h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredResults.suppliers.map((supplier) => (
                    <Link
                      key={supplier.id}
                      href={`/${locale}/suppliers/${supplier.id}`}
                      className="group overflow-hidden rounded-3xl border-2 p-6 transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: cssVars.neutral.surface,
                        borderColor: cssVars.neutral.border,
                      }}
                    >
                      <div className="mb-4 flex items-center gap-4">
                        <img
                          src={supplier.avatar}
                          alt={supplier.name}
                          className="h-16 w-16 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <h3
                              className="text-xl font-bold transition-opacity group-hover:opacity-70"
                              style={{ color: cssVars.secondary.DEFAULT }}
                            >
                              {supplier.name}
                            </h3>
                            {supplier.isVerified && (
                              <span style={{ color: cssVars.status.success }}>✓</span>
                            )}
                          </div>
                          <p className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                            {supplier.serviceDescription}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div style={{ color: cssVars.neutral.textSecondary }}>
                          ⭐ {supplier.rating} ({supplier.totalReviews})
                        </div>
                        <div style={{ color: cssVars.neutral.textSecondary }}>
                          {supplier.completedProjects} {t('projects')}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
